import axios from 'axios';
import { Sidebar } from '..';
import { DarkModeToggle } from '../Layout/DarkModeToggle';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom'; // Added useLocation
import { useCookies } from 'react-cookie';
import { useEffect, useState, useRef } from 'react';
import { ArrowLeftIcon, ArrowLineDownIcon, ArrowRightIcon, CheckIcon } from '@phosphor-icons/react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import GlobalSidebarItem from '../components/sidebar/GlobalSidebar/GlobalSidebarItem';
import Overview from './components/Overview';
import PerformanceAnalysis from './components/PerformanceAnalysis';
import ScorePotential from './components/ScorePotential';
import AttemptAnalysis from './components/AttemptAnalysis';
import TimeAnalysis from './components/TimeAnalysis';
import DifficultyAnalysis from './components/DifficultyAnalysis';
import SubjectMovement from './components/SubjectMovement';
import QuestionJourney from './components/QuestionJourney';
import QuestionByQuestionAnalysis from './components/QuestionByQuestionAnalysis';
import downloadAnalysis from './services/downloadAnalysis';
import DownloadAnalysisModal from './components/DownloadAnalysisModal';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import TorchGuide from '../components/generic/TorchGuide';
import { AppLayout } from '../Layout/AppLayout';
import { usePackStorage } from '../Layout/usePackStorage';

const tabDescriptionMapping = {
  Overview: 'This section helps you understand your performance in the test attempts.',
  'Performance Analysis':
    'This section helps you quickly analyse your performance measured in terms of attempts that were correct, incorrect, unattempted and questions that were not visited at all.',
  'Score Potential':
    'This section categories the question(s) you got wrong in the test into respective chapters & topics so that you can analyze your mistakes and work on them.',
  'Attempt Analysis': "Know how you're solving questions in your attempted test. Go through the definitions first.",
  'Time Analysis':
    'Time matters in competitive exams. This section shows time spent, attempt rate, and accuracy per subject—helping you balance speed and precision.',
  'Difficulty Analysis':
    'Every exam has Easy, Moderate, and Tough questions. This section shows your accuracy in each, helping you focus on getting the easy and moderate ones right.',
  'Subject Movement':
    'This displays your question attempts for a specific subject within a given time frame. Multiple attempts at the same question will be counted separately if you revisit and change your answer for particular qs in that subject.',
  'Question Journey': 'This section shows how you moved through question during the test for 3 hours',
  'Qs by Qs Analysis': 'This gives you quick snapshot of your attempts subject-wise as well as question-type wise.',
};

const steps = [
  {
    target: '.comparative-target',
    content: 'Click here to view comparative analysis and compare your performance with other students.',
    title: 'View Comparative Analysis',
    disableBeacon: true,
    spotlightPadding: 0,
  },
  {
    target: '.detailed-analysis-target',
    content: 'Click here to view different types of analysis to evaluate your performance better.',
    title: 'Detailed Analysis',
    placement: 'right',
  },
  {
    target: '.download-analysis-target',
    content: 'Click here to download the AYJR Paper in PDF format',
    title: 'Download Paper',
    spotlightPadding: 5,
  },
];

export default function TestAnalysis() {
  const { packId, testId } = useParams();
  const navigate = useNavigate();
  const location = useLocation(); // Added useLocation hook
  const [cookies] = useCookies();
  const { purchasedPacks, selectedPack, handlePackChange } = usePackStorage();
  const [searchParams, setSearchParams] = useSearchParams();

  const user = JSON.parse(localStorage.getItem('userProfile'));
  const headers = sessionStorage.getItem('token') ? { Authorization: `Bearer ${sessionStorage.getItem('token')}` } : {};

  const [breadcrumbItems, setBreadcrumbItems] = useState([]);

  // tour guide states
  const [stepIndex, setStepIndex] = useState(0);
  const [showTorchGuide, setShowTorchGuide] = useState(true);

  // loading state
  const [isLoading, setIsLoading] = useState(true);

  // test states
  const [test, setTest] = useState(null);
  const [totalMarks, setTotalMarks] = useState(null);

  // download analysis state
  const [downloadingAnalysisDisable, setdownloadingAnalysisDisable] = useState(false);
  const [aiAnalysisAvailable, setAiAnalysisAvailable] = useState(false);
  const [showDownloadAnalysisModal, setShowDownloadAnalysisModal] = useState(false);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);

  // state for comparative and personal analysis
  const [hideComparativeAnalysis, setHideComparativeAnalysis] = useState(true);
  const [activeAnalysisTab, setActiveAnalysisTab] = useState('personal');

  // state for selected tab
  const [tabsToShow, setTabsToShow] = useState([]);
  const [tabIconsMapping, setTabIconsMapping] = useState(null);
  const [selectedTab, setSelectedTab] = useState(null);

  // check if analysis is avaiable
  const [detailedAnalysisAvailable, setDetailedAnalysisAvailable] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);

  // analysis states
  const [percentile, setPercentile] = useState(null);
  const [overviewData, setOverviewData] = useState(null);
  const [comparativeData, setComparativeData] = useState(null);
  const [paidFeatures, setPaidFeatures] = useState(null);

  // Get category ID from URL params or location state
  const categoryId = searchParams.get('categoryId') || location.state?.selectedCategoryId;
  const source = searchParams.get('source') || location.state?.source;

  // Scroll states for hide-on-scroll behavior
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const scrollContainerRef = useRef(null);

  // Function to handle back navigation with category ID
  const handleBackNavigation = () => {
    if (categoryId) {
      navigate('/v2/home');
    } else {
      if (source === 'timeline_view') {
        navigate(`/v3/${packId}/timelineview`);
        return;
      }
      navigate(`/v2/home`);
    }
  };

  // Cycle text every 2 seconds when AI analysis is available
  useEffect(() => {
    if (!aiAnalysisAvailable) return;

    const interval = setInterval(() => {
      setCurrentTextIndex((prev) => (prev + 1) % 2);
    }, 4000);

    return () => clearInterval(interval);
  }, [aiAnalysisAvailable]);

  useEffect(() => {
    const checkIfTablet = () => {
      setShowTorchGuide(window.innerWidth > 1024);
    };

    checkIfTablet();
    window.addEventListener('resize', checkIfTablet);
    return () => window.removeEventListener('resize', checkIfTablet);
  }, []);

  // Scroll handler for hide-on-scroll behavior
  useEffect(() => {
    const handleScroll = () => {
      if (!scrollContainerRef.current) return;

      const currentScrollY = scrollContainerRef.current.scrollTop;
      const isTabletOrBelow = window.innerWidth < 1024; // lg breakpoint

      if (!isTabletOrBelow) {
        // Always show header on desktop
        setIsHeaderVisible(true);
        return;
      }

      // Hide/show logic for tablet and mobile
      if (currentScrollY < lastScrollY) {
        // Scrolling up
        setIsHeaderVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down and past 100px
        setIsHeaderVisible(false);
      }

      setLastScrollY(currentScrollY);
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [lastScrollY]);

  useEffect(() => {
    if (!test) {
      axios
        .get(`/api/test/${packId}/${testId}`, { headers: headers })
        .then((response) => {
          if (response?.data?.packExpired) {
            navigate('/v2/home');
          } else {
            if (response?.data?.isAttempted) {
              setShowAnalysis(true);
            } else {
              navigate(`/${packId}/${testId}/details`);
            }
            if (response?.data?.basicAnalysis?.aiReportGenerated) {
              setAiAnalysisAvailable(true);
            }
            if (response?.data && response?.data?.subjects) {
              const marks = {
                overall: response?.data?.totalMarks,
                ...response?.data?.subjects.reduce((acc, subject) => {
                  acc[subject.subjectId._id] = subject.totalMarks;
                  return acc;
                }, {}),
              };
              setTotalMarks(marks);
            }
            const breadcrumbItems = [
              {
                title: response?.data?.exam[0]?.title,
                onClick: () => {
                  handleBackNavigation();
                },
              },
              {
                title: response?.data?.title,
              },
              {
                title: 'Analysis',
              },
              {
                title: 'Overview',
              },
            ];
            setBreadcrumbItems(breadcrumbItems);
            setTest(response?.data);
            setIsLoading(false);
            setPercentile(response?.data?.percentile);
            sessionStorage.setItem('testObject', JSON.stringify(response?.data));
            const isDetailedAnalysisAvailable =
              response?.data?.isAttempted && response?.data?.isStarted && response?.data?.analysisAllowed;
            setDetailedAnalysisAvailable(isDetailedAnalysisAvailable);
          }
        })
        .catch((err) => {});
    }

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`/api/test/analysis/v2/${packId}/${testId}/overview`);
        setOverviewData(response?.data?.data);
        setTabsToShow(response?.data?.data?.tabsToShow);
        setTabIconsMapping(response?.data?.data?.tabIconsMapping);
        setSelectedTab(response?.data?.data?.tabsToShow?.[0]);
        setHideComparativeAnalysis(response?.data?.data?.hideComparative);
        setPaidFeatures(response?.data?.data?.paidFeatures);
      } catch (err) {
        try {
          const fallbackResponse = await axios.get(`/api/test/detailedAnalysis/${testId}`);
          if (fallbackResponse.status === 200) {
            window.location.reload();
          }
        } catch (fallbackErr) {}
      } finally {
        setIsLoading(false);
      }
    };
    if (!overviewData) {
      fetchData();
    }
  }, [packId, testId]);

  useEffect(() => {
    if (activeAnalysisTab === 'comparative') {
      setSelectedTab('Performance Analysis');
      if (!comparativeData) {
        setIsLoading(true);
        axios.get(`/api/test/comparativeAnalysis/${packId}/${testId}`).then((res) => {
          setComparativeData(res?.data);
          setIsLoading(false);
        });
      }
    } else {
      setSelectedTab(tabsToShow?.[0]);
    }
  }, [activeAnalysisTab]);

  const handleDownloadAnalysis = async () => {
    if (!aiAnalysisAvailable) {
      window.open(test.paperPdf, '_blank');
    } else {
      setShowDownloadAnalysisModal(true);
    }
  };

  const handleTabChange = (tab) => {
    setSelectedTab(tab);
    // update title of last breadcrumb item
    setBreadcrumbItems((prevItems) => {
      const updated = [...prevItems];
      if (updated.length > 3) {
        updated[3] = { ...updated[3], title: tab };
      }
      return updated;
    });
  };

  const handleJoyrideCallback = async (data) => {
    const { status, index, type, action } = data;

    if (status === 'finished') {
      setStepIndex(0);
      await axios.patch('/api/user/profile', {
        analysisV3TourDone: true,
      });
      localStorage.setItem('userProfile', JSON.stringify({ ...user, analysisV3TourDone: true }));
    } else if (type === 'step:after') {
      if (action === 'next') {
        setStepIndex(index + 1);
      } else if (action === 'prev') {
        setStepIndex(Math.max(0, index - 1));
      }
    }
  };

  return (
    <AppLayout purchasedPacks={purchasedPacks} selectedPack={selectedPack} onPackChange={handlePackChange}>
      <div className="lg:hidden q3-glass fixed border-t bottom-0 z-20 bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)] w-full p-4">
        <div className="flex flex-row">
          {!hideComparativeAnalysis && (
            <div className="flex flex-row">
              <Select
                value={activeAnalysisTab === 'comparative' ? 'Comparative' : 'Personal'}
                onValueChange={(val) => {
                  setActiveAnalysisTab(val.toLowerCase());
                }}
              >
                <SelectTrigger
                  className={`rounded-full flex items-center gap-2 px-4 py-2 bg-[var(--q3-surface-default)] text-[var(--q3-accent-normal)] border border-[var(--q3-stroke-normal)] hover:bg-[var(--q3-surface-dimmest)] transition-colors w-auto cursor-pointer [&>svg]:text-[var(--q3-accent-normal)]`}
                >
                  <SelectValue />
                </SelectTrigger>

                <SelectContent className="rounded-md border border-[var(--q3-stroke-normal)] bg-[var(--q3-surface-default)] shadow-md max-h-64 overflow-y-auto cursor-pointer">
                  <SelectGroup>
                    {[
                      {
                        key: 'personal',
                        value: 'Personal',
                      },
                      {
                        key: 'comparative',
                        value: 'Comparative',
                      },
                    ].map((item) => (
                      <SelectItem key={item.key} value={item.value}>
                        {item.value}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <div className="border-r ml-[11px]" />
            </div>
          )}
          <div className="flex flex-row gap-[11px] ml-[11px] overflow-x-auto">
            {activeAnalysisTab === 'personal'
              ? tabsToShow?.map((tab) => {
                  return (
                    <Button
                      variant={'outline'}
                      className={`rounded-full ${selectedTab === tab ? '!bg-[var(--q3-neutral-default)] text-[var(--q3-neutral-default_inverted)] hover:!text-[var(--q3-neutral-default_inverted)] hover:!bg-[var(--q3-neutral-default)]' : 'hover:bg-[var(--q3-surface-dimmest)]'}`}
                      onClick={() => handleTabChange(tab)}
                      key={`tab-${tab}`}
                    >
                      {tab}
                      {selectedTab === tab && <CheckIcon />}
                    </Button>
                  );
                })
              : ['Performance Analysis', 'Attempt Analysis', 'Difficulty Analysis'].map((tab) => {
                  return (
                    <Button
                      variant={'outline'}
                      className={`rounded-full ${selectedTab === tab ? '!bg-[var(--q3-neutral-default)] text-[var(--q3-neutral-default_inverted)] hover:!text-[var(--q3-neutral-default_inverted)] hover:!bg-[var(--q3-neutral-default)]' : 'hover:bg-[var(--q3-surface-dimmest)]'}`}
                      onClick={() => handleTabChange(tab)}
                      key={`tab-${tab}`}
                    >
                      {tab}
                      {selectedTab === tab && <CheckIcon />}
                    </Button>
                  );
                })}
          </div>
        </div>
      </div>

      {/* Torch Guide */}
      {!user?.analysisV3TourDone && showTorchGuide && (
        <TorchGuide steps={steps} handleTorchGuideCallback={handleJoyrideCallback} stepIndex={stepIndex} />
      )}

      <div className="flex flex-row max-w-[1300px] mx-auto flex-1 m-0 p-0 overflow-hidden">
        {/* Side Section */}
        <div className="hidden lg:flex flex-col w-[264px] items-start pl-6">
          <div className="py-6 px-4 flex flex-col items-start">
            <div
              className="text-[var(--q3-neutral-default)] cursor-pointer bg-[var(--q3-surface-dimmer)] rounded-full p-2"
              onClick={handleBackNavigation} // Updated to use the new function
            >
              <ArrowLeftIcon size={24} />
            </div>
            <div className="text-[var(--q3-neutral-default)] text-2xl font-bold my-2">Test Analysis</div>
            <div className="text-sm text-[var(--q3-neutral-light)]">{test?.title}</div>
          </div>
          <div className="w-full">
            {!hideComparativeAnalysis ? (
              <Tabs defaultValue="personal" onValueChange={(value) => setActiveAnalysisTab(value)}>
                <TabsList className="w-full mb-2">
                  <TabsTrigger value="personal">
                    Personal {activeAnalysisTab === 'personal' && <CheckIcon size={20} weight="bold" />}
                  </TabsTrigger>
                  <TabsTrigger value="comparative" className="pl-3 pr-2 py-2 comparative-target">
                    Comparative {activeAnalysisTab === 'comparative' && <CheckIcon size={20} weight="bold" />}
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="personal" className={'detailed-analysis-target'}>
                  {tabsToShow?.map((tab, index) => (
                    <GlobalSidebarItem
                      key={index}
                      icon={tabIconsMapping?.[tab]}
                      title={tab}
                      isSelected={tab === selectedTab}
                      onClick={() => handleTabChange(tab)}
                      className={`w-full`}
                    />
                  ))}
                </TabsContent>
                <TabsContent value="comparative">
                  {['Performance Analysis', 'Attempt Analysis', 'Difficulty Analysis'].map((tab, index) => (
                    <GlobalSidebarItem
                      key={index}
                      icon={tabIconsMapping?.[tab]}
                      title={tab}
                      isSelected={tab === selectedTab}
                      onClick={() => handleTabChange(tab)}
                      className="w-full"
                    />
                  ))}
                </TabsContent>
              </Tabs>
            ) : (
              <div>
                {tabsToShow?.map((tab, index) => (
                  <GlobalSidebarItem
                    key={index}
                    icon={tabIconsMapping?.[tab]}
                    title={tab}
                    isSelected={tab === selectedTab}
                    onClick={() => handleTabChange(tab)}
                    className="w-full"
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex flex-col w-full h-screen overflow-y-auto" ref={scrollContainerRef}>
          <div
            className={`z-10 flex flex-col lg:flex-row justify-between p-6 pb-4 q3-glass rounded transition-transform duration-300 ease-in-out lg:translate-y-0 ${
              isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
            }`}
          >
            <div className="hidden lg:flex gap-2 flex-col items-start">
              <p className="text-2xl text-[var(--q3-neutral-default)] font-bold whitespace-nowrap">{selectedTab}</p>
            </div>
            <div className="flex w-full items-start justify-between">
              <div
                className="block lg:hidden cursor-pointer text-[var(--q3-neutral-default)] bg-[var(--q3-surface-dimmer)] rounded-full p-2"
                onClick={handleBackNavigation} // Updated to use the new function
              >
                <ArrowLeftIcon size={24} />
              </div>
              <div className="flex gap-2 ml-auto">
                <Button
                  variant="outline"
                  className="group relative inline-flex h-10 overflow-hidden rounded-full p-[1px] focus:outline-none download-analysis-target"
                  onClick={handleDownloadAnalysis}
                >
                  <span className="absolute inset-[-1000%] animate-[spin_2s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,#E2CBFF_0%,#393BB2_50%,#E2CBFF_100%)]" />
                  <span className="relative inline-flex h-full w-full cursor-pointer items-center justify-center rounded-full bg-[var(--q3-surface-dim)] px-3 py-1 text-sm font-medium text-[var(--q3-neutral-default)] backdrop-blur-3xl overflow-hidden">
                    <ArrowLineDownIcon
                      size={22}
                      className="transition-transform duration-300 group-hover:translate-y-0.5"
                    />
                    <div className="hidden lg:block relative ml-2 h-[1.25rem] overflow-hidden">
                      <div
                        className={`transition-transform duration-500 ease-in-out ${
                          aiAnalysisAvailable
                            ? currentTextIndex === 0
                              ? 'translate-y-0'
                              : '-translate-y-[1.25rem]'
                            : 'translate-y-0'
                        }`}
                      >
                        <p className="h-[1.25rem] flex items-center">Download Paper</p>
                      </div>
                    </div>
                  </span>
                </Button>

                <Button
                  className={
                    'bg-[var(--q3-accent-normal)] rounded-full h-10 flex flex-row items-center justify-center transition-colors duration-300 hover:bg-[var(--q3-accent-dark)] group'
                  }
                  onClick={() => navigate(`/${packId}/${testId}/solutions`)}
                >
                  <p className="pl-2">View Solution</p>
                  <ArrowRightIcon size={22} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                </Button>
              </div>
            </div>

            <div className="lg:hidden mt-2 flex flex-col gap-2 items-start">
              <div>
                <p className="text-2xl text-[var(--q3-neutral-default)] font-bold">{selectedTab}</p>
              </div>
            </div>
          </div>
          <div className="p-6 pt-4 pb-28">
            {selectedTab === 'Overview' ? (
              <Overview
                overviewData={overviewData}
                percentile={percentile}
                analysisVideo={test?.analysisVideo}
                analysisVideoCdn={test?.analysisVideoCdn}
                analysisVideoThumbnail={test?.analysisVideoThumbnail}
                notes={test?.basicAnalysis?.notes}
                testTitle={test?.title}
                packId={packId}
                testId={testId}
              />
            ) : selectedTab === 'Performance Analysis' ? (
              <PerformanceAnalysis
                isComparative={activeAnalysisTab === 'comparative'}
                comparativeData={comparativeData}
                totalMarks={totalMarks}
                packId={packId}
                testId={testId}
              />
            ) : selectedTab === 'Score Potential' ? (
              <ScorePotential packId={packId} testId={testId} />
            ) : selectedTab === 'Attempt Analysis' ? (
              <AttemptAnalysis
                isComparative={activeAnalysisTab === 'comparative'}
                comparativeData={comparativeData}
                packId={packId}
                testId={testId}
              />
            ) : selectedTab === 'Time Analysis' ? (
              <TimeAnalysis
                isComparative={activeAnalysisTab === 'comparative'}
                comparativeData={comparativeData}
                packId={packId}
                testId={testId}
                totalTime={test?.totalTime}
              />
            ) : selectedTab === 'Difficulty Analysis' ? (
              <DifficultyAnalysis
                packId={packId}
                testId={testId}
                isComparative={activeAnalysisTab === 'comparative'}
                comparativeData={comparativeData}
              />
            ) : selectedTab === 'Subject Movement' ? (
              <div className="overflow-x-auto">
                <SubjectMovement packId={packId} testId={testId} />
              </div>
            ) : selectedTab === 'Question Journey' ? (
              <QuestionJourney packId={packId} testId={testId} totalTime={test?.totalTime} />
            ) : selectedTab === 'Qs by Qs Analysis' ? (
              <QuestionByQuestionAnalysis packId={packId} testId={testId} />
            ) : (
              <></>
            )}
          </div>
        </div>
        {showDownloadAnalysisModal && (
          <DownloadAnalysisModal
            isOpen={showDownloadAnalysisModal}
            onClose={() => setShowDownloadAnalysisModal(false)}
            packId={packId}
            testId={testId}
            testTitle={test?.title}
            userId={user._id}
            userEmail={user?.email}
            userToken={cookies.token}
            setdownloadingAnalysisDisable={setdownloadingAnalysisDisable}
          />
        )}
      </div>
    </AppLayout>
  );
}
